import {
	type CollectionSummaryJSONObject,
	EXISTING_VARIANT_GROUP,
	type FastExportType,
	type IkeActionFastExport,
	type VariantExportSelection,
	variantSelectionKey,
} from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import JSZip from 'jszip';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { getLongRunningTaskManager } from '../long.running.task.manager';
import { PDFReportMaker, type ReportTypeEnum } from '../report/ReportMaker';

/**
 * Resolves the parent collections' `variants` refs matching the `(variantType, variantTypeName)`
 * pair to full `CollectionModel`s — the client-side analog of the server's
 * `export.variants.common.ts#resolveGroupCollections`, needed here because PDF reports are
 * generated entirely in the browser (pdfMake), never touching the export task system.
 */
async function resolveVariantCollectionModels(
	selection: VariantExportSelection,
	parents: CollectionModel[]
): Promise<CollectionModel[]> {
	if (selection.variantType === EXISTING_VARIANT_GROUP) {
		return parents;
	}

	const uuids = new Set<string>();
	for (const parent of parents) {
		for (const ref of parent.variants ?? []) {
			if (
				variantSelectionKey(ref.variantType, ref.variantTypeName) ===
				variantSelectionKey(selection.variantType, selection.variantTypeName)
			) {
				uuids.add(ref.variantUuid);
			}
		}
	}

	const models = await Promise.all(
		Array.from(uuids).map(async (uuid) => {
			const summary = await AppStore.CollectionSummary.fetchByUuid(uuid);
			// A parent can still reference a deleted variant; the lookup resolves null for it.
			// Skip that one rather than fail the whole export.
			if (summary == null) {
				return null;
			}
			return AppStore.Collection.fetch(summary.revision);
		})
	);
	return models.filter((model): model is CollectionModel => model != null);
}

/** Strips characters that can't appear in a zip entry/file name. */
function sanitizeFileName(name: string): string {
	return name.replace(/[/\\?%*:|"<>]/g, '_');
}

/** Renders a pdfMake document to a `Blob` — same callback-to-promise shape used by the plain PDF export. */
function pdfToBlob(pdf: Awaited<ReturnType<typeof PDFReportMaker.makeReport>>): Promise<Blob> {
	return new Promise((resolve, reject) => {
		try {
			pdf.getBlob((blob: Blob) => resolve(blob));
		} catch (err) {
			reject(err);
		}
	});
}

export class ExportMenuControllerImpl {
	hasOCalcForm(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary != null && formSummary.metadata != null && formSummary.metadata.ocalc != null) {
				return true;
			}
		}

		return false;
	}

	hasSPIDACalcForm(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary != null && formSummary.metadata != null && formSummary.metadata.spidacalc != null) {
				return true;
			}
		}

		return false;
	}

	hasPoleforeman(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary?.metadata?.poleforeman) {
				return true;
			}
		}

		return false;
	}

	hasIkePoleForeman(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary?.metadata?.ikePoleForeman) {
				return true;
			}
		}

		return false;
	}

	hasDDS(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary?.metadata?.dds) {
				return true;
			}
		}

		return false;
	}

	// ── Fast exports (synchronous — binary stream response) ──

	async exportKML(options?: { silent?: boolean }): Promise<string> {
		return this.executeFastExport('KML', options);
	}

	async exportJSON(options?: { silent?: boolean }): Promise<string> {
		return this.executeFastExport('JSON', options);
	}

	// ── Long-running exports (async — task system + download manager) ──

	/**
	 * One KML file per selected variant-export group (named after the checkbox label the user
	 * saw, e.g. Existing.kml, Proposed.kml), zipped together when more than one group is
	 * selected. Unlike the plain `exportKML`, this runs through the long-running task system
	 * since it may need to resolve and render several groups of collections.
	 */
	async exportKMLVariants(selections: VariantExportSelection[], options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'KML_VARIANTS',
			revisions,
			'KML',
			this.getExportLabel(),
			{
				variantGroups: selections,
				silent: options?.silent,
			}
		);
	}

	/**
	 * One JSON file per selected variant-export group (named after the checkbox label the user
	 * saw, e.g. Existing.json, Proposed.json), zipped together when more than one group is
	 * selected. Unlike the plain `exportJSON`, this runs through the long-running task system
	 * since it may need to resolve and render several groups of collections.
	 */
	async exportJSONVariants(selections: VariantExportSelection[], options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'JSON_VARIANTS',
			revisions,
			'JSON',
			this.getExportLabel(),
			{
				variantGroups: selections,
				silent: options?.silent,
			}
		);
	}

	async exportExcel(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask('EXCEL', revisions, 'Excel', this.getExportLabel(), {
			silent: options?.silent,
		});
	}

	/**
	 * One Excel file per selected variant-export group (named after the checkbox label the user
	 * saw, e.g. Existing.xlsx, Proposed.xlsx), zipped together when more than one group is
	 * selected. Unlike the plain `exportExcel`, this runs through the long-running task system
	 * since it may need to resolve and render several groups of collections.
	 */
	async exportExcelVariants(selections: VariantExportSelection[], options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'EXCEL_VARIANTS',
			revisions,
			'Excel',
			this.getExportLabel(),
			{
				variantGroups: selections,
				silent: options?.silent,
			}
		);
	}

	async exportImportableExcel(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'IMPORTABLE_EXCEL',
			revisions,
			'Importable Excel',
			this.getExportLabel(),
			{ silent: options?.silent }
		);
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group is placed in its own folder inside the one OCalc zip, holding every pole's file for that
	 * group (OCalc always exports one file per pole, never merged) — see the sibling handling in
	 * `export.ocalc.ts`. Without it, the whole selection is exported as before this feature existed.
	 */
	async exportOCalc(options?: { silent?: boolean }, selections?: VariantExportSelection[]): Promise<string> {
		const revisions = this.getRevisions((summary) => {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			return formSummary != null && formSummary.metadata != null && formSummary.metadata.ocalc != null;
		});
		return getLongRunningTaskManager().service.startExportTask('OCALC', revisions, 'OCalc', this.getExportLabel(), {
			silent: options?.silent,
			variantGroups: selections,
		});
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group is placed in its own folder inside the one SPIDACalc zip, merging collections from
	 * every selected job into that folder — see the sibling handling in `export.spidacalc.ts`.
	 * Without it, the whole selection is exported as before this feature existed.
	 */
	async exportSPIDACalc(
		noPhotos: boolean,
		options?: { silent?: boolean },
		selections?: VariantExportSelection[]
	): Promise<string> {
		const revisions = this.getRevisions((summary) => {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			return formSummary != null && formSummary.metadata != null && formSummary.metadata.spidacalc != null;
		});
		return getLongRunningTaskManager().service.startExportTask(
			'SPIDACALC',
			revisions,
			'SPIDACalc',
			this.getExportLabel(),
			{
				noPhotos,
				silent: options?.silent,
				variantGroups: selections,
			}
		);
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group is placed in its own folder inside the one Poleforeman zip, merging collections from
	 * every selected job into that folder — see the shared handling in `GroupedExporter.export()`.
	 * Without it, the whole selection is exported as before this feature existed.
	 */
	async exportPoleforeman(options?: { silent?: boolean }, selections?: VariantExportSelection[]): Promise<string> {
		const revisions = this.getRevisions((summary) => {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			return formSummary != null && formSummary.metadata != null && formSummary.metadata.poleforeman != null;
		});
		return getLongRunningTaskManager().service.startExportTask(
			'POLEFOREMAN',
			revisions,
			'Poleforeman',
			this.getExportLabel(),
			{ variantGroups: selections }
		);
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group is placed in its own folder inside the one IPF zip, merging collections from every
	 * selected job into that folder — see the shared handling in `GroupedExporter.export()`.
	 * Without it, the whole selection is exported as before this feature existed.
	 */
	async exportIkePoleForeman(options?: { silent?: boolean }, selections?: VariantExportSelection[]): Promise<string> {
		const collectionSummaries: CollectionSummaryJSONObject[] = [];
		for (const summary of CurrentState.Tag.collections) {
			const formSummary = await AppStore.FormSummary.getAsync(summary.formRevision);
			if (formSummary?.metadata?.ikePoleForeman) {
				collectionSummaries.push(summary);
			}
		}
		const revisions = collectionSummaries.map((s) => s.revision);
		return getLongRunningTaskManager().service.startExportTask(
			'IKE_POLEFOREMAN',
			revisions,
			'IPF',
			this.getExportLabel(),
			{
				silent: options?.silent,
				variantGroups: selections,
			}
		);
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group is placed in its own folder inside the one DDS zip, merging collections from every
	 * selected job into that folder — see the shared handling in `GroupedExporter.export()`.
	 * Without it, the whole selection is exported as before this feature existed.
	 */
	async exportDDS(options?: { silent?: boolean }, selections?: VariantExportSelection[]): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask('DDS', revisions, 'DDS', this.getExportLabel(), {
			silent: options?.silent,
			variantGroups: selections,
		});
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group's images go in their own folder inside the one zip — see the shared handling in
	 * `export.imagezip.ts`. Without it, the whole selection is exported as before this feature existed.
	 */
	async exportImageZip(options?: { silent?: boolean }, selections?: VariantExportSelection[]): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'IMAGE_ZIP',
			revisions,
			'Images',
			this.getExportLabel(),
			{
				silent: options?.silent,
				variantGroups: selections,
			}
		);
	}

	/**
	 * When `selections` is given (the "Include variants" checkboxes were checked), each selected
	 * group is placed in its own folder inside the one shapefile zip — see the sibling handling in
	 * `export.shapefile.ts`. Without it, the whole selection is bundled flat, exactly as before
	 * this feature existed.
	 */
	async exportShapefile(options?: { silent?: boolean }, selections?: VariantExportSelection[]): Promise<string | void> {
		const response = await ModalService.open({
			title: Translate(T.EXPORT_SHAPEFILE_WGS84_TITLE),
			content: Translate(T.EXPORT_SHAPEFILE_WGS84_BODY),
			hasCancel: true,
			okName: T.CONTINUE,
		});
		if (!response.ok) return;

		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'SHAPEFILE',
			revisions,
			'Shapefile',
			this.getExportLabel(),
			{
				silent: options?.silent,
				variantGroups: selections,
			}
		);
	}

	// ── PDF reports (client-side — unchanged) ──

	/**
	 * Creates a PDF report of collections and downloads it
	 * @param type The type of report to export
	 */
	async exportReport(type: ReportTypeEnum) {
		const revisions = CurrentState.Tag.collections.map((summary) => summary.revision);
		const collectionModels = await Promise.all(revisions.map((revision) => AppStore.Collection.fetch(revision)));

		return PDFReportMaker.makeReport(type, collectionModels);
	}

	/**
	 * Variant-aware counterpart to `exportReport`: one PDF per selected "Include variants" group —
	 * still merging every selected job into that one PDF, exactly like the plain report — with the
	 * variant name in each PDF's filename. Zipped together only when more than one PDF is produced.
	 */
	async exportReportVariants(
		type: ReportTypeEnum,
		selections: VariantExportSelection[],
		reportLabel: string
	): Promise<{ blob: Blob; fileName: string }> {
		const revisions = CurrentState.Tag.collections.map((summary) => summary.revision);
		const parents = (await Promise.all(revisions.map((revision) => AppStore.Collection.fetch(revision)))).filter(
			(model): model is CollectionModel => model != null
		);

		const dateString = new Date().toDateString();
		const files: { blob: Blob; fileName: string }[] = [];

		for (const selection of selections) {
			const models = await resolveVariantCollectionModels(selection, parents);
			if (models.length === 0) {
				continue;
			}
			const blob = await pdfToBlob(await PDFReportMaker.makeReport(type, models));
			files.push({ blob, fileName: sanitizeFileName(`${reportLabel} ${dateString} - ${selection.label}.pdf`) });
		}

		const [first] = files;
		if (first == null) {
			throw new Error('No collections found for the selected variants');
		}
		if (files.length === 1) {
			return first;
		}

		const zip = new JSZip();
		for (const file of files) {
			zip.file(file.fileName, file.blob);
		}
		return { blob: await zip.generateAsync({ type: 'blob' }), fileName: `${reportLabel} ${dateString}.zip` };
	}

	// ── Export orchestration ──

	async runExport(func: () => Promise<string | string[] | void>): Promise<string | string[] | void> {
		try {
			return await func();
		} catch (_) {
			NotificationService.error({ key: T.ERROR_GENERAL });
		}
	}

	// ── Private helpers ──

	/**
	 * Execute a fast export (KML, JSON) via the `export.fast` action.
	 * Registers the export in the download manager so it appears as a row.
	 * Uses `actionRaw()` because the server streams binary data on success
	 * (not JSON), which the standard `action()` / `_post()` cannot handle.
	 */
	private async executeFastExport(type: FastExportType, options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const jobName = this.getExportLabel();

		const taskManager = getLongRunningTaskManager();
		const taskId = taskManager.service.addFastExportTask(type, jobName, { silent: options?.silent });

		try {
			const response = await Services.Department.rest.actionRaw<IkeActionFastExport>('export.fast', {
				type,
				revisions,
				timezone,
			});

			if (!response.ok) {
				taskManager.service.failFastExportTask(taskId, undefined, 'Export failed');
				return taskId;
			}

			const disposition = response.headers.get('Content-Disposition') || '';

			if (!disposition.includes('attachment')) {
				// Error/warning path: server returned notifications instead of file data
				const json = await response.json();
				if (json.notifications != null && json.notifications.length > 0) {
					taskManager.service.failFastExportTask(taskId, json.notifications);
				} else {
					taskManager.service.failFastExportTask(taskId, undefined, 'Export failed');
				}
				return taskId;
			}

			// Success path: file response — download as blob
			const blob = await response.blob();
			const fileNameMatch = disposition.match(/filename="([^"]+)"/);
			const fileName = fileNameMatch ? fileNameMatch[1]! : `export.${type.toLowerCase()}`;
			const url = URL.createObjectURL(blob);
			taskManager.service.completeFastExportTask(taskId, url, fileName);
		} catch (_err) {
			taskManager.service.failFastExportTask(taskId, undefined, 'Export failed');
		}
		return taskId;
	}

	private getRevisions(filter?: (summary: CollectionSummaryJSONObject) => boolean): string[] {
		let collections = CurrentState.Tag.collections;
		if (filter != null) {
			collections = collections.filter(filter);
		}
		return collections.map((summary) => summary.revision);
	}

	getExportLabel(): string {
		const collections = CurrentState.Tag.collections;
		const tagIds = CurrentState.Tag.tags;

		if (collections.length === 1) {
			// Single pole: "Job title - Collection title"
			const collection = collections[0]!;
			const tagId = collection.tags[0];
			const jobName = tagId ? AppStore.Tag.get(tagId)?.name : null;
			const title = collection.title || 'Export';
			return jobName ? `${jobName} - ${title}` : title;
		}

		if (tagIds.length === 1) {
			// Multiple collections within same job: "Job title - X collections"
			const jobName = AppStore.Tag.get(tagIds[0]!)?.name;
			return jobName ? `${jobName} - ${collections.length} collections` : `${collections.length} collections`;
		}

		// Multiple collections from multiple jobs: "X jobs - Y collections"
		return `${tagIds.length} jobs - ${collections.length} collections`;
	}
}

export const ExportMenuController = new ExportMenuControllerImpl();
